import React from "react";
import styled from "styled-components";
import { Modal, ModalSizes } from "@interstate/components/Modal";

// Styled container for footer buttons with proper spacing and background
export const FooterButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  width: 100%;
  box-sizing: border-box;
`;

interface StyledModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** The modal title */
  title: string;
  /** The main content of the modal */
  children: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Optional test ID for the modal container */
  containerTestId?: string;
  /** Optional maximum width for the modal */
  maxWidth?: string;
  /** Optional maximum height for the modal */
  maxHeight?: string;
  /** Optional minimum height for the modal */
  minHeight?: string;
  /** Optional fixed height for the modal */
  height?: string;
  /** Optional size for the modal (defaults to large) */
  size?: ModalSizes;
}

export function StyledModal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  containerTestId,
  maxWidth,
  maxHeight,
  minHeight,
  height,
  size = "large"
}: StyledModalProps) {
  // Format footer for Interstate Modal if provided
  const interstateFooter = footer
    ? {
        footerComponent: footer
      }
    : undefined;

  const modalStyles = {
    // Modal container sizing and positioning
    "& .MuiDialog-paper": {
      maxWidth: maxWidth || "800px",
      maxHeight: height ? undefined : maxHeight || "calc(100vh - 80px)",
      minHeight: minHeight || undefined,
      height: height || undefined,
      marginTop: "80px",
      borderRadius: "8px",
      padding: 0
    },
    // Header styling - custom colors and spacing
    "& .ids-modal-title": {
      padding: "20px 24px 16px",
      backgroundColor: "#f8f9fa",
      borderBottom: "1px solid #e0e0e0"
    },
    // Title text styling
    "& .interstate-modal-title": {
      color: "#333333",
      fontSize: "1.25rem",
      fontWeight: "600"
    },
    // Content area - remove padding for edge-to-edge content
    "& .MuiDialogContent-root": {
      padding: "0 !important",
      overflow: "visible"
    },
    // Footer area - remove padding (FooterButtonContainer handles its own styling)
    "& .MuiDialogActions-root": {
      padding: 0
    },
    "& .MuiDialogContent-root p": {
      margin: "revert",
      fontSize: "revert",
      color: "revert",
      fontFamily: "revert",
      fontWeight: "revert",
      lineHeight: "revert",
      fontVariationSettings: "revert"
    }
  };

  return (
    <Modal
      data-testid={containerTestId}
      footer={interstateFooter}
      header={title}
      id={containerTestId}
      show={isOpen}
      size={size}
      sx={modalStyles}
      onHide={onClose}
    >
      {children}
    </Modal>
  );
}

export default StyledModal;
